import isNil from "lodash/isNil";

import { ModalPath } from "@/constants/Navigation";
import { router } from "@/crossPlatform/expo-router";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import { captureException, captureMessage } from "@/services/AppMonitor";
import {
  useDeleteWaitingRoomAssessmentMutation,
  useLazyGetNextWaitingRoomAssessmentQuery,
} from "@/store/api/waitingRoom";
import { useLazyGetZoomUrlQuery, ZoomMeetingType } from "@/store/api/zoom";
import { useAppSelector } from "@/store/hooks";
import {
  selectWaitingRoomData,
  useWaitingRoomModalDispatch,
} from "@/store/reducers/modals/waitingRoomModal";
import { getTimeRange } from "@/utils/date.util";
import { openUrl } from "@/utils/navigation.util";
import {
  clearWaitingRoomReservation,
  getWaitingRoomReservation,
  saveWaitingRoomReservation,
} from "@/utils/waitingRoomStorage.util";

interface InterceptJoinPressParams {
  endDate: string;
  eventId: string;
  eventTitle: string;
  isGroupClass?: boolean;
  patientId: string;
  startDate: string;
}

interface UseWaitingRoomIntercept {
  deleteWaitingRoomReservation: () => Promise<void>;
  dismissWaitingRoom: () => void;
  fetchNextAssessment: () => Promise<boolean>;
  interceptJoinPress: (params: InterceptJoinPressParams) => Promise<boolean>;
  joinZoom: () => Promise<void>;
  skipWaitingRoom: () => Promise<void>;
}

const useWaitingRoomIntercept = (): UseWaitingRoomIntercept => {
  const { resetWaitingRoomData, setWaitingRoomData } =
    useWaitingRoomModalDispatch();
  const waitingRoomData = useAppSelector(selectWaitingRoomData);
  const [getNextWaitingRoomAssessment] =
    useLazyGetNextWaitingRoomAssessmentQuery();
  const [deleteWaitingRoomAssessment] =
    useDeleteWaitingRoomAssessmentMutation();
  const [getZoomUrl] = useLazyGetZoomUrlQuery();
  const { isGroupClassRedesignEnabled } = useFeatureFlags();

  const interceptJoinPress = async ({
    endDate,
    eventId,
    eventTitle,
    isGroupClass = false,
    patientId,
    startDate,
  }: InterceptJoinPressParams): Promise<boolean> => {
    if (!patientId || !eventId || isGroupClass) return true;

    const { data } = await getNextWaitingRoomAssessment({
      eventId,
      patientId,
    });

    if (isNil(data) || isNil(data.assessmentId)) return true;
    const timeRange = getTimeRange({ endDate, startDate });

    setWaitingRoomData({
      assessmentId: data.assessmentId,
      endDate,
      eventId,
      eventTitle,
      hasMore: data.hasMore,
      isGroupClass,
      patientId,
      startDate,
      timeRange,
    });
    saveWaitingRoomReservation({
      assessmentId: data.assessmentId,
      eventId,
      patientId,
    });
    router.navigate({ pathname: ModalPath.WaitingRoom });
    return false;
  };

  const skipWaitingRoom = async (): Promise<void> => {
    if (!waitingRoomData?.patientId || !waitingRoomData.assessmentId) return;

    await deleteWaitingRoomAssessment({
      assessmentId: waitingRoomData.assessmentId,
      eventId: waitingRoomData.eventId,
      patientId: waitingRoomData.patientId,
    }).unwrap();
    clearWaitingRoomReservation();
  };

  const fetchNextAssessment = async (): Promise<boolean> => {
    if (!waitingRoomData?.patientId) return false;
    const { data } = await getNextWaitingRoomAssessment({
      eventId: waitingRoomData.eventId,
      patientId: waitingRoomData.patientId,
    });

    if (isNil(data) || isNil(data.assessmentId)) return false;

    setWaitingRoomData({
      ...waitingRoomData,
      assessmentId: data.assessmentId,
      hasMore: data.hasMore,
    });
    saveWaitingRoomReservation({
      assessmentId: data.assessmentId,
      eventId: waitingRoomData.eventId,
      patientId: waitingRoomData.patientId,
    });
    return true;
  };

  const joinZoom = async (): Promise<void> => {
    const waitingRoomEventId = waitingRoomData?.eventId;
    const isWaitingRoomGroupClass = waitingRoomData?.isGroupClass;
    dismissWaitingRoom();
    if (!waitingRoomEventId) return;
    const { data } = await getZoomUrl({
      externalId: waitingRoomEventId,
      isGroupClassRedesignEnabled,
      type: isWaitingRoomGroupClass
        ? ZoomMeetingType.GroupClassEvent
        : ZoomMeetingType.AppointmentEvent,
    });
    if (isNil(data) || isNil(data.joinUrl)) return;
    await openUrl({
      onError: (error) => {
        captureException(
          new Error("Failed to open zoom from waiting room", {
            cause: error,
          }),
        );
      },
      url: data.joinUrl,
    });
  };

  const deleteWaitingRoomReservation = async (): Promise<void> => {
    const reservation = getWaitingRoomReservation();
    if (!reservation) return;
    await deleteWaitingRoomAssessment(reservation).catch(() =>
      captureMessage("Failure deleting waiting room assessment"),
    );
    clearWaitingRoomReservation();
  };

  const dismissWaitingRoom = () => {
    resetWaitingRoomData();
    void deleteWaitingRoomReservation();
    if (!router.canDismiss()) return;
    router.dismiss();
  };

  return {
    deleteWaitingRoomReservation,
    dismissWaitingRoom,
    fetchNextAssessment,
    interceptJoinPress,
    joinZoom,
    skipWaitingRoom,
  };
};

export default useWaitingRoomIntercept;
